// Typing rhythm without key values (docs/05-thinking-page.md): intervals, deletions, bursts.

export interface TypingBatch {
  [key: string]: number;
  keys: number;
  mean_interval_ms: number;
  median_interval_ms: number;
  p90_interval_ms: number;
  deletions: number;
  max_burst_chars: number;
}

const BURST_GAP_MS = 1000;

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  const index = Math.min(sorted.length - 1, Math.ceil((p / 100) * sorted.length) - 1);
  return sorted[Math.max(0, index)] ?? 0;
}

export class TypingTracker {
  private lastKeyAt: number | null = null;
  private intervals: number[] = [];
  private keys = 0;
  private deletions = 0;
  private burst = 0;
  private maxBurst = 0;

  /** Record one keystroke; `deletion` for Backspace/Delete. */
  key(atMs: number, deletion: boolean): void {
    if (this.lastKeyAt !== null) {
      const gap = atMs - this.lastKeyAt;
      this.intervals.push(gap);
      this.burst = gap < BURST_GAP_MS ? this.burst + 1 : 1;
    } else {
      this.burst = 1;
    }
    this.maxBurst = Math.max(this.maxBurst, this.burst);
    this.lastKeyAt = atMs;
    this.keys += 1;
    if (deletion) this.deletions += 1;
  }

  /** Stats since the last flush, or null when nothing was typed. Gaps carry across batches. */
  flush(): TypingBatch | null {
    if (this.keys === 0) return null;
    const sorted = [...this.intervals].sort((a, b) => a - b);
    const mean = sorted.length ? sorted.reduce((a, b) => a + b, 0) / sorted.length : 0;
    const batch: TypingBatch = {
      keys: this.keys,
      mean_interval_ms: Math.round(mean),
      median_interval_ms: percentile(sorted, 50),
      p90_interval_ms: percentile(sorted, 90),
      deletions: this.deletions,
      max_burst_chars: this.maxBurst,
    };
    this.intervals = [];
    this.keys = 0;
    this.deletions = 0;
    this.maxBurst = this.burst;
    return batch;
  }
}
